/**
 * Configurazione globale cache ottimizzata per performance
 * CRITICAL: TTL sincronizzati tra SSR e Data cache
 * 
 * REGOLA: TTL Data >= TTL SSR per evitare che l'HTML sia valido
 * ma con dati sottostanti già scaduti
 */

// Cache warming interval - REFERENCE per tutti i TTL
export const CACHE_WARMING_INTERVAL = 30 * 60 * 1000; // 30 minuti

// Configurazione cache warming - ABILITATA per performance ottimali
export const CACHE_WARMING_CONFIG = {
  enabled: true,
  intervalMinutes: 30,
  backgroundRefresh: true,
  preloadCities: ['Roma', 'Milano', 'Napoli', 'Torino', 'Palermo', 'Firenze', 'Bologna', 'Bari'],
  maxCacheAge: 4 * 60 * 60 * 1000, // 4 ore
};

// High-Frequency Warming Configuration (ogni 1 minuto per pagine con prezzi real-time)
export const HIGH_FREQUENCY_WARMING_CONFIG = {
  enabled: true,
  intervalMinutes: 1,
  pages: ['/', '/avvisi'],
};

// TTL cache ottimizzati - SINCRONIZZATI tra SSR e Data
export const CACHE_TTL = {
  // === SSR CACHE (HTML renderizzato) ===
  // Deve essere >= warming interval per garantire sempre cache valida
  SSR_HIGH_FREQUENCY: 90 * 1000,   // 90 sec (homepage, /avvisi) - 1.5x warming interval 1 min
  SSR_CRITICAL: 35 * 60 * 1000,    // 35 min (/compro-oro)
  SSR_HIGH: 2 * 60 * 60 * 1000,    // 2 ore (città, dealer, investimenti)
  SSR_MEDIUM: 35 * 60 * 1000,      // 35 min (blog, diamanti)
  SSR_LOW: 45 * 60 * 1000,         // 45 min (altre pagine)
  
  // === DATA CACHE (dati sottostanti) ===
  // ALLINEATI con SSR per evitare discrepanze
  SSR_DEALERS: 2 * 60 * 60 * 1000, // 2 ore - allineato con SSR_HIGH
  SSR_PRICES: 30 * 60 * 1000,      // 30 min - dati dinamici
  SSR_STATS: 2 * 60 * 60 * 1000,   // 2 ore - allineato con SSR_HIGH
  SSR_CITIES: 2 * 60 * 60 * 1000,  // 2 ore - allineato con SSR_HIGH (era 6 ore)
  
  // === CLIENT CACHE ===
  CLIENT_DEALERS: 15 * 60 * 1000,  // 15 min
  CLIENT_PRICES: 5 * 60 * 1000,    // 5 min
  CLIENT_BLOG: 3 * 60 * 60 * 1000, // 3 ore
  CLIENT_CITIES_STATS: 30 * 60 * 1000, // 30 min - allineato con warming
  CLIENT_GEOLOCATION: 5 * 60 * 1000, // 5 min - cache posizione utente (allineato a geolocationSlice)
  
  // === CACHE DI CALCOLO ===
  SPREAD_CACHE: 30 * 60 * 1000,    // 30 min - allineato con warming
  PRICE_CALCULATION: 10 * 60 * 1000, // 10 min
  
  // === MAP CACHE (dati mappa) ===
  MAP_BOUNDS: 2 * 60 * 60 * 1000,      // 2 ore - dati bounds regionali
  MAP_POSITIONS: 4 * 60 * 60 * 1000,   // 4 ore - posizioni dealer
  MAP_ICONS: 24 * 60 * 60 * 1000,      // 24 ore - icone marker
  MAP_CLUSTERS: 2 * 60 * 60 * 1000,    // 2 ore - cluster pre-calcolati
  
  // === IMAGE HTTP CACHE (browser) ===
  IMAGE_CACHE_MAX_AGE: 30 * 24 * 60 * 60, // 30 giorni in secondi (2592000)
};

// === HTTP CACHE TTL (CDN/Browser in seconds) ===
// Usati per Cache-Control headers nelle risposte HTML e immagini
// NOTA: prod-server.js usa questi valori come reference documentale,
// ma implementa la logica direttamente per compatibilità ESM runtime
export const HTTP_CACHE_TTL = {
  // === HIGH-FREQUENCY (/ e /avvisi) - Warming ogni 1 minuto ===
  BROWSER_HIGH_FREQUENCY: 60,   // 1 min - browser cache
  CDN_HIGH_FREQUENCY: 60,       // 1 min - CDN cache (s-maxage)
  STALE_HIGH_FREQUENCY: 120,    // 2 min - stale-while-revalidate
  
  // HTML pages - Browser cache
  BROWSER_CRITICAL: 120,      // 2 min - /compro-oro (senza home)
  BROWSER_HIGH: 300,          // 5 min - city pages, dealer
  BROWSER_MEDIUM: 120,        // 2 min - diamonds, charts
  BROWSER_LOW: 300,           // 5 min - legal, auth
  
  // HTML pages - CDN cache (s-maxage)
  CDN_CRITICAL: 300,          // 5 min - /compro-oro (senza home)
  CDN_HIGH: 7200,             // 2 ore - city pages, dealer
  CDN_MEDIUM: 1800,           // 30 min - diamonds, charts
  CDN_LOW: 3600,              // 1 ora - legal, auth
  
  // Stale-while-revalidate (allows serving stale content while fetching fresh)
  STALE_CRITICAL: 600,        // 10 min
  STALE_HIGH: 21600,          // 6 ore
  STALE_MEDIUM: 3600,         // 1 ora
  STALE_LOW: 7200,            // 2 ore
  
  // Images - Browser cache
  BROWSER_IMAGE: 2592000,     // 30 giorni
  CDN_IMAGE: 31536000,        // 1 anno
  STALE_IMAGE: 86400,         // 1 giorno
  
  // Image proxy miss (original served before optimization)
  BROWSER_IMAGE_MISS: 604800, // 7 giorni
  CDN_IMAGE_MISS: 2592000,    // 30 giorni
  
  // Static assets (immutable - versioned by hash)
  STATIC_IMMUTABLE: 31536000, // 1 anno
  
  // Bot requests (longer cache for SEO crawlers)
  BOT_BROWSER: 600,           // 10 min
  BOT_CDN: 3600,              // 1 ora
  BOT_STALE: 86400,           // 1 giorno
};

// Stale times per SWR pattern (50% del TTL corrispondente)
export const STALE_TIMES = {
  HIGH_FREQUENCY: 45 * 1000, // 45 sec (home, /avvisi - < warming 60s)
  CRITICAL: 15 * 60 * 1000,  // 15 min (50% di 35 min)
  HIGH: 60 * 60 * 1000,      // 1 ora (50% di 2 ore)
  MEDIUM: 15 * 60 * 1000,    // 15 min (50% di 35 min)
  LOW: 22 * 60 * 1000,       // 22 min (50% di 45 min)
};

// Configurazione hit rate monitoring
export const CACHE_MONITORING = {
  minHitRate: 70, // Soglia minima hit rate accettabile
  optimizationInterval: 5 * 60 * 1000, // Ogni 5 minuti
  cleanupInterval: 10 * 60 * 1000, // Ogni 10 minuti
  logCacheStats: true,
};

// Environment variables per cache warming
export const getCacheEnvConfig = () => ({
  ENHANCED_CACHE_ENABLED: process.env.ENHANCED_CACHE_ENABLED || 'true',
  ENHANCED_CACHE_PRELOAD: process.env.ENHANCED_CACHE_PRELOAD || 'true',
  ENHANCED_CACHE_BACKGROUND_REFRESH: process.env.ENHANCED_CACHE_BACKGROUND_REFRESH || 'true',
  ENHANCED_CACHE_REFRESH_INTERVAL: process.env.ENHANCED_CACHE_REFRESH_INTERVAL || '30',
});

// Funzione per verificare se cache warming è abilitato
export const isCacheWarmingEnabled = (): boolean => {
  const envConfig = getCacheEnvConfig();
  return envConfig.ENHANCED_CACHE_ENABLED === 'true';
};

// Configurazione cache per tipologia di pagina - USA COSTANTI CENTRALIZZATE
export const PAGE_CACHE_CONFIG = {
  HOME: { ttl: CACHE_TTL.SSR_CRITICAL, stale: STALE_TIMES.CRITICAL, preload: true },
  COMPRO_ORO: { ttl: CACHE_TTL.SSR_CRITICAL, stale: STALE_TIMES.CRITICAL, preload: true },
  CITY: { ttl: CACHE_TTL.SSR_HIGH, stale: STALE_TIMES.HIGH, preload: true },
  CITIES_PAGE: { ttl: CACHE_TTL.SSR_HIGH, stale: STALE_TIMES.HIGH, preload: true },
  DEALER_DETAIL: { ttl: CACHE_TTL.CLIENT_DEALERS, stale: STALE_TIMES.MEDIUM, preload: false },
  BLOG: { ttl: CACHE_TTL.CLIENT_BLOG, stale: STALE_TIMES.HIGH, preload: true },
  API: { ttl: CACHE_TTL.SSR_PRICES, stale: STALE_TIMES.CRITICAL, preload: true },
};

// Utility per convertire TTL da ms a ore (per persistentCache)
export const ttlToHours = (ttlMs: number): number => ttlMs / (60 * 60 * 1000);
